import { useEffect, useRef, useState, RefObject } from 'react';
import { useStoreState } from 'base/store';
import { useMutationObserver, useSize } from 'ahooks';
import delay from 'base/utils/delay';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import { hasClass } from 'ava-util/dist/dom-class';

type IUseProductTagScale = {
    listRef?: HTMLElement | null;
    warpId?: string; // list容器id名 （有的列表(//详情页搭配商品列表)出现获取不到listRef容器宽度， 通过id重新获取）
    resizable?: boolean; // 容器宽高变化监听
    monitorData: any; // 监听的数据变化
    multiple?: number; // 缩放倍数系数
    ratioConfig?: {
        parentW?: number; // 替换父元素默认宽度
    }
}

const useProductTagScale = (props: IUseProductTagScale): RefObject<HTMLDivElement> => {
    const listRef = useRef<HTMLDivElement>(null);
    const { ua: { isPhone } } = useStoreState('rootStore');

    const defaultConfig = {
        parentW: isPhone ? 185 : 288, // 图片容器 默认的宽度设置
    }

    const {
        listRef: propsListRef,
        warpId,
        resizable = true,
        monitorData,
        ratioConfig = {},
        multiple = 1
    } = props || {}

    const [retryCount, setReTryCount] = useState<number>(0)

    let listWrapEl = typeof propsListRef !== 'undefined' ? propsListRef : listRef.current;
    if (!listWrapEl && typeof window != 'undefined') {
        listWrapEl = document.getElementById(warpId || '')
    }

    // 列表容器宽高
    const wrapSize = useSize(listWrapEl);

    const config = {
        ...defaultConfig,
        ...ratioConfig
    }

    const {
        parentW: defaultPicW,
    } = config

    /**
     * 计算列表缩放比
     */
    const tagScaleHandle = () => {
        if (!listWrapEl) {
            return
        }

        let childrenWrap = listWrapEl.querySelectorAll('.js-picture')

        if (childrenWrap && childrenWrap.length > 0) {
            // 图片容器宽度
            let picWrapW = (childrenWrap[0] as HTMLDivElement)?.offsetWidth

            if (!picWrapW && warpId) {
                // 搭配商品取不到宽度值（估计是 commonSwiper 影响）。。。
                // 重新通过id赋值 childrenWrap 和 picWrapW
                const _pWrapEl = document.getElementById(warpId || '')
                childrenWrap = _pWrapEl?.querySelectorAll('.js-picture') || childrenWrap
                const el = _pWrapEl?.querySelector('.js-picture')
                picWrapW = (el as HTMLDivElement)?.offsetWidth
            }

            const ratio = (picWrapW / defaultPicW)

            childrenWrap.forEach((elWrap) => {
                // 商品标签
                const proTagEls = elWrap.querySelectorAll('.js-product-tag')
                // 活动标签组
                const activeTagsEl = elWrap.querySelectorAll('.js-active-tags');

                if (proTagEls.length) {
                    proTagEls.forEach(el => {
                        (el as HTMLDivElement).style.transform = `scale(${(ratio * multiple) || 1})`;
                        if (hasClass(el, 'js-full')) {
                            (el as HTMLDivElement).style.width = `${defaultPicW}px`;
                        }
                    })
                }

                if (activeTagsEl.length > 0) {
                    Array.prototype.forEach.call(activeTagsEl, el => {
                        el.style.transform = `scale(${(ratio * multiple) || 1})`
                    })
                }
            })
        }
    }

    /**
     * 列表Dom 节点树变化监听
     * https://developer.mozilla.org/zh-CN/docs/Web/API/MutationObserver/observe
     */
    useMutationObserver(
        () => {
            tagScaleHandle()
        },
        listWrapEl,
        {
            childList: true,
            subtree: true
        }
    )

    // 检查列表容器元素
    const checkListWrapSize = async () => {
        if (!monitorData) {
            return
        }
        await delay(50)
        //没有找到 50ms 重新执行render
        if (!listWrapEl) {
            // 最大重复次数
            if (retryCount < 100) {
                setReTryCount(retryCount + 1)
            }
        }
    }
    checkListWrapSize().then()

    // 数据、列表变化
    useEffect(() => {
        tagScaleHandle()
    }, [monitorData, listWrapEl])

    // 容器尺寸变化
    useEffect(() => {
        if (resizable) {
            if (wrapSize) {
                tagScaleHandle()
            } else if (listWrapEl) {
                // 有获取到元素 但没有size 监听窗口宽度（又是详情页搭配商品）
                addEvent(window, 'resize', tagScaleHandle)
                return () => {
                    removeEvent(window, 'resize', tagScaleHandle)
                }
            }
        }
    }, [resizable, wrapSize])

    return listRef;
}

export default useProductTagScale;